'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@repo/ui/components/sheet';
import { StatusBadge, type StatusTone } from '@repo/ui/components/status-badge';

import {
  HYGIENE_CHECK_STATUSES,
  type HygieneCategory,
  type HygieneCheck,
  type HygieneCompleted
} from '../types/hygiene';
import {
  categoryTagline,
  categoryTitle,
  checkExplains,
  checkStatusLabel,
  checkTitle,
  measurementLine,
  pendingLine,
  pendingOwnerLine
} from '../utils/hygiene-presentation';

interface HygieneCategorySheetProps {
  category: HygieneCategory | null;
  hygiene: HygieneCompleted | null;
  onClose: () => void;
}

// A status this build has never seen is neutral: the app does not know
// whether it is good news.
const STATUS_TONE: Record<string, StatusTone> = {
  met: 'positive',
  partially_met: 'warning',
  not_met: 'error',
  not_evaluable: 'neutral',
  not_applicable: 'neutral'
};

const KNOWN_STATUSES: readonly string[] = HYGIENE_CHECK_STATUSES;

const PRIORITY_ORDER: Record<string, number> = { now: 0, soon: 1, monitor: 2 };

/** A priority this build has never seen ranks after the known ones. */
function actionRank(check: HygieneCheck): number {
  if (!check.action) return 9;
  const priority = check.action.priority;
  return priority != null ? (PRIORITY_ORDER[priority] ?? 3) : 3;
}

function byActionThenSeverity(left: HygieneCheck, right: HygieneCheck): number {
  const leftCritical = left.severity === 'critical';
  if (leftCritical !== (right.severity === 'critical')) {
    return leftCritical ? -1 : 1;
  }
  return actionRank(left) - actionRank(right);
}

function ActionLink({ check }: { check: HygieneCheck }) {
  if (
    !check.action ||
    check.action.destination.type !== 'route' ||
    !check.action.destination.href
  ) {
    return null;
  }
  return (
    <Link
      href={check.action.destination.href}
      className="text-rowa-label-md text-rowa-text-brand mt-2 inline-flex items-center gap-1 font-medium hover:opacity-80"
    >
      {check.action.label}
      <ArrowRight className="size-3.5" aria-hidden />
    </Link>
  );
}

/** Full row: a check the reader should read (problem or confirmation). */
function CheckRow({ check }: { check: HygieneCheck }) {
  const measurement = measurementLine(check);
  const showCritical =
    check.severity === 'critical' &&
    (check.status === 'not_met' || check.status === 'partially_met');

  return (
    <li className="border-rowa-border-subtle border-b py-4 first:pt-2 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={STATUS_TONE[check.status] ?? 'neutral'}>
          {checkStatusLabel(check.status)}
        </StatusBadge>
        {showCritical ? <StatusBadge tone="error">Critical</StatusBadge> : null}
        <p className="text-rowa-body-md text-rowa-text-primary min-w-0 flex-1 font-medium">
          {checkTitle(check)}
        </p>
      </div>
      <p className="text-rowa-body-sm text-rowa-text-secondary mt-1.5">
        {checkExplains(check)}
      </p>
      {measurement ? (
        <p className="text-rowa-label-md text-rowa-text-primary mt-2 tabular-nums">
          {measurement}
        </p>
      ) : null}
      <ActionLink check={check} />
    </li>
  );
}

/** Compact row: a check that is waiting on data — calm, one line of why. */
function WaitingRow({
  check,
  hygiene
}: {
  check: HygieneCheck;
  hygiene: HygieneCompleted;
}) {
  return (
    <li className="border-rowa-border-subtle border-b py-3 first:pt-2 last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-rowa-body-md text-rowa-text-primary min-w-0 font-medium">
          {checkTitle(check)}
        </p>
        <p className="text-rowa-label-sm text-rowa-text-tertiary">
          {pendingOwnerLine(check, hygiene)}
        </p>
      </div>
      <p className="text-rowa-body-sm text-rowa-text-secondary mt-1">
        {pendingLine(check, hygiene)}
      </p>
      <ActionLink check={check} />
    </li>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-rowa-label-sm text-rowa-text-secondary mt-6 tracking-[0.08em] uppercase first:mt-3">
      {children}
    </h3>
  );
}

/**
 * Category drill-down, ordered by value to the reader: what needs their
 * attention, what's confirmed healthy, then what's still waiting on data
 * (our move, one calm section — never equal billing).
 */
export function HygieneCategorySheet({
  category,
  hygiene,
  onClose
}: HygieneCategorySheetProps) {
  const attention = category
    ? category.checks
        .filter(
          (check) =>
            check.status === 'not_met' || check.status === 'partially_met'
        )
        .sort(byActionThenSeverity)
    : [];
  const healthy = category
    ? category.checks.filter((check) => check.status === 'met')
    : [];
  const waiting = category
    ? category.checks
        .filter((check) => check.status === 'not_evaluable')
        .sort((left, right) => actionRank(left) - actionRank(right))
    : [];
  const notApplicable = category
    ? category.checks.filter((check) => check.status === 'not_applicable')
        .length
    : 0;
  // A check in a status this build has never seen still belongs on the
  // sheet. It is listed under its own served status rather than dropped.
  const other = category
    ? category.checks.filter((check) => !KNOWN_STATUSES.includes(check.status))
    : [];

  return (
    <Sheet
      open={category != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent side="right" className="overflow-y-auto sm:max-w-lg">
        {category && hygiene ? (
          <>
            <SheetHeader>
              <SheetTitle>{categoryTitle(category)}</SheetTitle>
              <SheetDescription>{categoryTagline(category)}</SheetDescription>
            </SheetHeader>
            <div className="px-4 pb-6">
              <p className="text-rowa-label-md text-rowa-text-secondary">
                {category.score != null
                  ? `Scores ${Math.round(category.score)} of 100 · ${Number(category.weight)}% of your overall score`
                  : `No score yet, waiting on data · ${Number(category.weight)}% of your overall score`}
              </p>

              {attention.length > 0 ? (
                <>
                  <SectionHeading>Needs your attention</SectionHeading>
                  <ul className="list-none">
                    {attention.map((check) => (
                      <CheckRow key={check.id} check={check} />
                    ))}
                  </ul>
                </>
              ) : null}

              {healthy.length > 0 ? (
                <>
                  <SectionHeading>Looking good</SectionHeading>
                  <ul className="list-none">
                    {healthy.map((check) => (
                      <CheckRow key={check.id} check={check} />
                    ))}
                  </ul>
                </>
              ) : null}

              {waiting.length > 0 ? (
                <>
                  <SectionHeading>Waiting on data</SectionHeading>
                  <p className="text-rowa-body-sm text-rowa-text-secondary mt-2">
                    These fill in on their own as data connects. Your score is
                    never docked while they wait.
                  </p>
                  <ul className="mt-1 list-none">
                    {waiting.map((check) => (
                      <WaitingRow
                        key={check.id}
                        check={check}
                        hygiene={hygiene}
                      />
                    ))}
                  </ul>
                </>
              ) : null}

              {other.length > 0 ? (
                <>
                  <SectionHeading>Other checks</SectionHeading>
                  <ul className="list-none">
                    {other.map((check) => (
                      <CheckRow key={check.id} check={check} />
                    ))}
                  </ul>
                </>
              ) : null}

              {notApplicable > 0 ? (
                <p className="text-rowa-label-sm text-rowa-text-tertiary mt-6">
                  {notApplicable === 1
                    ? "1 check doesn't apply to your account type."
                    : `${notApplicable} checks don't apply to your account type.`}
                </p>
              ) : null}
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
